package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.StoreAppDto
import dev.lingvo.shared.StoreConnectionDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import dev.lingvo.site.state.runProjectAction
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap

internal data class StoresPageData(
    val project: ProjectDetailDto,
    val orgSlug: String,
    val apps: List<StoreAppDto>,
    /** Null when this role may not list the workspace's connections, or the list failed. */
    val connections: List<StoreConnectionDto>?,
    val connectionsFailure: String? = null,
)

/**
 * Every store request this screen makes on someone's behalf, run under one captured claim.
 *
 * Connecting, binding, importing, pushing, retrying and rolling back all reach a live store, and the
 * server gates every one of them on owner-or-admin. The claim is the Glossary page's: captured for
 * [ProjectPrivilege.STRUCTURE], re-checked before the request and after it, so a role change or a
 * project switch mid-request retires the result instead of publishing it into the wrong screen.
 */
internal class StoreActionRunner(
    val canManage: Boolean,
    private val claim: ProjectActionClaim?,
    private val slug: String,
    private val launch: (suspend () -> Unit) -> Unit,
    val notify: (String, LToastVariant, Long) -> Int,
    private val onVaultOff: () -> Unit,
    private val currentRoute: () -> dev.lingvo.site.state.VerifiedProjectRoute? = { liveProjectRoute(slug) },
    private val currentMemberships: () -> List<dev.lingvo.shared.WorkspaceDto> = { Session.organizations },
) {
    /**
     * @param onError returns true when it explained the failure itself; otherwise a toast does.
     */
    fun <T> run(
        setBusy: (Boolean) -> Unit,
        failure: String,
        transport: suspend () -> T,
        onError: (Throwable) -> Boolean = { false },
        onSuccess: (T) -> Unit,
    ) {
        if (!canManage) return
        val live = claim
            ?.takeIf { it.remainsAuthorized(currentRoute(), currentMemberships()) }
            ?: return
        launch {
            runProjectAction(
                claim = live,
                currentRoute = currentRoute,
                currentMemberships = currentMemberships,
                setBusy = setBusy,
                transport = transport,
                onSuccess = onSuccess,
                onFailure = { error ->
                    if (error.isStoreCode(STORE_VAULT_UNCONFIGURED)) onVaultOff()
                    if (!onError(error)) {
                        notify(storeErrorMessage(error, failure), LToastVariant.DANGER, TOAST_UNTIL_DISMISSED)
                    }
                },
            )
        }
    }
}

@Page("/app/projects/{slug}/stores")
@Layout(".components.layouts.AppLayout")
@Composable
fun ProjectStoresPage() {
    val slug = rememberPageContext().route.params["slug"].orEmpty()
    val verifiedRoute = liveProjectRoute(slug)
    val authority = ProjectAuthority.resolve(verifiedRoute?.project, Session.organizations)
    val authoritySubject = verifiedRoute?.authoritySubject(Session.organizations)
    val structureClaim = ProjectActionClaim.capture(
        verifiedRoute,
        Session.organizations,
        ProjectPrivilege.STRUCTURE,
    )
    var refreshKey by remember(authoritySubject) { mutableStateOf(0) }
    val state = loadReloadable(authoritySubject, reloadToken = refreshKey) {
        val subject = authoritySubject ?: awaitCancellation()
        requestProjectResource(
            key = subject.action,
            expectedSlug = slug,
            memberships = Session.organizations,
        ) { route -> loadStoresPage(route.project) }
    }
    val toasts = rememberToasts()

    Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
        when (val snapshot = state.state) {
            Async.Loading -> {
                StoresHeader()
                LoadingState(rows = 5)
            }
            is Async.Failure -> {
                StoresHeader()
                AsyncFailureState(
                    snapshot,
                    subject = t("app.projects.slug.stores.the_store_listings", "the store listings"),
                    onRetry = { refreshKey++ },
                )
            }
            is Async.Success -> StoresContent(
                data = snapshot.value,
                authority = authority,
                subject = authoritySubject,
                claim = structureClaim,
                slug = slug,
                onBindingChanged = { refreshKey++ },
                notify = toasts::show,
            )
        }
    }
    ToastHost(toasts)
}

private suspend fun loadStoresPage(project: ProjectDetailDto): StoresPageData {
    val orgSlug = project.orgSlug.ifBlank {
        Session.organizations.firstOrNull { it.id == project.orgId }?.slug.orEmpty()
    }
    val apps = ApiClient.storeApps(project.slug)
    val canManage = ProjectAuthority.resolve(project, Session.organizations).canEditStructure
    if (!canManage || orgSlug.isBlank()) return StoresPageData(project, orgSlug, apps, connections = null)
    return try {
        StoresPageData(project, orgSlug, apps, ApiClient.storeConnections(orgSlug))
    } catch (failure: ApiException) {
        // The bound apps still render; only the connection list is missing, and the page says so.
        StoresPageData(
            project,
            orgSlug,
            apps,
            connections = null,
            connectionsFailure = storeErrorMessage(
                failure,
                tr("app.projects.slug.stores.couldnt_load_connections", "Couldn't load the workspace's store connections."),
            ),
        )
    }
}

@Composable
private fun StoresHeader() {
    PageHeader(
        title = t("app.projects.slug.stores.stores", "Stores"),
        subtitle = t(
            "app.projects.slug.stores.subtitle",
            "Your App Store and Google Play listings, translated with the rest of the project and pushed only after review.",
        ),
    )
}

@Composable
private fun StoresContent(
    data: StoresPageData,
    authority: ProjectAuthority,
    subject: ProjectAuthoritySubject?,
    claim: ProjectActionClaim?,
    slug: String,
    onBindingChanged: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
) {
    val scope = rememberCoroutineScope()
    var vaultOff by remember(subject) { mutableStateOf(false) }
    // Bumped by anything that changes what the listing holds: an import, a push, a rollback. The
    // diff and the history read it as a reload token, so they refresh without blanking.
    var listingRevision by remember(subject) { mutableStateOf(0) }
    val canManage = authority.canEditStructure
    val runner = StoreActionRunner(
        canManage = canManage,
        claim = claim,
        slug = slug,
        launch = { action -> scope.launch { action() } },
        notify = notify,
        onVaultOff = { vaultOff = true },
    )
    // An import also moves "last imported" on the app card, which lives in the page-level data, so
    // every change to the listing refreshes that too (without blanking the page).
    val onListingChanged: () -> Unit = {
        listingRevision++
        onBindingChanged()
    }
    val bindingChanged: () -> Unit = onListingChanged

    StoresHeader()
    if (vaultOff) {
        LAlert(
            t("app.projects.slug.stores.store_connections_are_off", "Store connections are turned off on this server"),
            storeVaultUnconfiguredMessage(),
            variant = LAlertVariant.WARNING,
            live = true,
        )
    }
    if (!canManage) {
        LAlert(
            t("app.projects.slug.stores.managed_by_owner_or_admin", "Stores are connected and pushed by an owner or admin"),
            t(
                "app.projects.slug.stores.managed_by_owner_or_admin_detail",
                "Connecting, importing and pushing change what customers read in a live store, so owners and " +
                    "admins do them. You can see what is bound, what would change and what went live.",
            ),
            variant = LAlertVariant.INFO,
        )
    }
    data.connectionsFailure?.let { message ->
        LAlert(
            t("app.projects.slug.stores.connections_unavailable", "Store connections are unavailable"),
            message,
            variant = LAlertVariant.WARNING,
        )
    }

    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
        LSectionHeading(
            title = t("app.projects.slug.stores.connect_heading", "Connect and import"),
            description = t(
                "app.projects.slug.stores.connect_description",
                "Connect each store once, bind this project's app, then import the listing that is live today.",
            ),
        )
        StorePlatform.entries.forEach { platform ->
            // Keyed by platform so each card keeps its own DOM nodes. Without it, when one card changes
            // shape (a connect form becomes a connection), Compose reuses its text areas for the next
            // card, and a private key typed into App Store Connect shows up in the Google Play form.
            key(platform.id) {
            StorePlatformCard(
                platform = platform,
                app = data.apps.firstOrNull { it.platform == platform },
                connections = data.connections,
                data = data,
                runner = runner,
                onBindingChanged = bindingChanged,
                onListingChanged = onListingChanged,
            )
            }
        }
    }

    val bound = StorePlatform.entries.mapNotNull { platform -> data.apps.firstOrNull { it.platform == platform } }
    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
        LSectionHeading(
            title = t("app.projects.slug.stores.review_heading", "Review and push"),
            description = t(
                "app.projects.slug.stores.review_description",
                "What the store shows today beside what Lingvo would send. Only approved text within the store's limits is pushed.",
            ),
        )
        when {
            bound.isEmpty() -> LMuted(
                t("app.projects.slug.stores.review_needs_binding", "Bind an app above and the comparison appears here."),
            )
            !authority.canWriteTranslations -> LMuted(
                t(
                    "app.projects.slug.stores.review_needs_translator",
                    "Comparing the listing with the store needs a translator role or above. The push history is below.",
                ),
            )
            else -> bound.forEach { app ->
                StoreDiffSection(
                    app = app,
                    subject = subject,
                    slug = slug,
                    revision = listingRevision,
                    runner = runner,
                    onListingChanged = onListingChanged,
                )
            }
        }
    }

    dev.lingvo.site.pages.app.projects.slug.screenshots.StoreScreenshotsSummary(slug)

    if (bound.isNotEmpty()) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            LSectionHeading(
                title = t("app.projects.slug.stores.history_heading", "History"),
                description = t(
                    "app.projects.slug.stores.history_description",
                    "Every push and rollback, who sent it, and what the store did with it.",
                ),
            )
            bound.forEach { app ->
                StoreHistorySection(
                    app = app,
                    subject = subject,
                    slug = slug,
                    revision = listingRevision,
                    runner = runner,
                    onListingChanged = onListingChanged,
                )
            }
        }
    }

    StoresLanguagesHelper(slug)
}

/** The listing is ordinary keys, so new languages come from the pages that already make them. */
@Composable
private fun StoresLanguagesHelper(slug: String) {
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        LSectionHeading(
            title = t("app.projects.slug.stores.add_languages_heading", "Add languages"),
            description = t(
                "app.projects.slug.stores.add_languages_description",
                "Add a language to the project, then draft its listing with pre-translate. The glossary " +
                    "keeps product names and terms consistent, and nothing is pushed until it is approved.",
            ),
        )
        Row(Modifier.gap(Space.SM).flexWrap(FlexWrap.Wrap)) {
            LLinkButton(
                t("app.projects.slug.stores.project_languages", "Project languages"),
                "/app/projects/$slug/settings?tab=languages",
                variant = LButtonVariant.SECONDARY,
            )
            LLinkButton(
                t("app.projects.slug.stores.pretranslate", "Pre-translate"),
                "/app/projects/$slug/pretranslate",
                variant = LButtonVariant.SECONDARY,
            )
            LLinkButton(
                t("app.projects.slug.stores.glossary", "Glossary"),
                "/app/projects/$slug/glossary",
                variant = LButtonVariant.GHOST,
            )
        }
    }
}
